import type { FlexDemoStep } from "./flexDemo.const";
import { FLEX_DEMO_PATH, FLEX_DEMO_STEPS } from "./flexDemo.const";
import type { FlexDemoWorkspaceTab } from "./flexDemo.session";
import { MY_CLUSTER_PATH } from "@/pages/my-cluster/myCluster.const";

export const FLEX_DEMO_EXPLORE_STEPS: readonly FlexDemoStep[] = [
  "explore",
  "preview",
  "express-interest",
  "auto-accept",
  "pay",
];

export const FLEX_DEMO_WORKSPACE_STEPS: readonly FlexDemoStep[] = ["workspace", "finish"];

export function isFlexDemoExploreStep(step: FlexDemoStep): boolean {
  return (FLEX_DEMO_EXPLORE_STEPS as readonly string[]).includes(step);
}

export function isFlexDemoWorkspaceStep(step: FlexDemoStep): boolean {
  return (FLEX_DEMO_WORKSPACE_STEPS as readonly string[]).includes(step);
}

export function flexDemoStepIndex(step: FlexDemoStep): number {
  return Math.max(0, FLEX_DEMO_STEPS.indexOf(step));
}

export type FlexDemoCoachCopy = {
  pageName: string;
  pageIsFor: string;
  nextClick: string;
  spotlight: string | null;
  /** When false, only a corner hint is shown so forms and modals stay clickable. */
  blocking?: boolean;
  /** Prefer docking the coach at the top (e.g. over a tall modal). */
  preferTopDock?: boolean;
};

function queryTour(id: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector(`[data-flex-demo-tour="${id}"]`) as HTMLElement | null;
}

function isEnabledButton(el: HTMLElement | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLButtonElement) return !el.disabled;
  const nested = el.querySelector("button");
  return nested ? !nested.disabled : true;
}

function isNdaChecked(el: HTMLElement | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLInputElement) return el.checked;
  const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
  return Boolean(input?.checked);
}

/**
 * Pay step: Proceed modal, then payment-success screen.
 * Spotlight Proceed first; once success opens, spotlight Go To Collaborations.
 */
export function resolvePayCoachFromDom(): FlexDemoCoachCopy | null {
  if (typeof document !== "undefined" && queryTour("go-collaborations")) {
    return {
      pageName: "Payment successful",
      pageIsFor:
        "Both parties have paid in this walkthrough. Open Collaborations next — that is where unlocked workspaces live.",
      nextClick: "Click Go To Collaborations.",
      spotlight: "go-collaborations",
      blocking: true,
      preferTopDock: true,
    };
  }
  return {
    pageName: "Unlock workspace",
    pageIsFor:
      "This is the live unlock modal. In production it can charge 1 collaboration. This walkthrough is waived and does not touch your wallet.",
    nextClick: "Click Proceed.",
    spotlight: "confirm-unlock",
    blocking: true,
    preferTopDock: true,
  };
}

/**
 * Progressive Express Interest coach — matches the live modal stage so we never
 * tell users to click Connect while only checkboxes / Generate / Preview are shown.
 */
export function resolveExpressInterestCoachFromDom(): FlexDemoCoachCopy {
  const base = {
    pageName: "Express interest",
    pageIsFor: "Work through this form step by step. The next action updates as you go.",
    preferTopDock: true as const,
  };

  const connect = queryTour("send-interest");
  if (connect && isEnabledButton(connect)) {
    return {
      ...base,
      pageIsFor: "You are on the final send step.",
      nextClick: "Click Connect.",
      spotlight: "send-interest",
      blocking: true,
    };
  }

  const nda = queryTour("accept-nda");
  if (nda && !isNdaChecked(nda)) {
    return {
      ...base,
      pageIsFor: "Connect stays locked until you accept the NDA.",
      nextClick: "Tick the NDA checkbox, then click Connect.",
      spotlight: "accept-nda",
      blocking: true,
    };
  }

  if (connect) {
    return {
      ...base,
      pageIsFor: "NDA accepted — you can send now.",
      nextClick: "Click Connect.",
      spotlight: "send-interest",
      blocking: true,
    };
  }

  const preview = queryTour("preview-synopsis");
  if (preview && isEnabledButton(preview)) {
    return {
      ...base,
      pageIsFor: "Pick two collaboration options, then preview what the partner will see.",
      nextClick: "Click Preview synopsis.",
      spotlight: "preview-synopsis",
      blocking: true,
    };
  }
  if (preview) {
    return {
      ...base,
      pageIsFor: "Select exactly two collaboration cards to continue.",
      nextClick: "Select two cards, then click Preview synopsis.",
      spotlight: null,
      blocking: false,
    };
  }

  const generate = queryTour("generate-eoi");
  if (generate && isEnabledButton(generate)) {
    return {
      ...base,
      pageIsFor: "Products and answers look ready.",
      nextClick: "Click Generate Expression Of Interest.",
      spotlight: "generate-eoi",
      blocking: true,
    };
  }

  return {
    ...base,
    pageIsFor: "Start by picking products/services and answering the match questions.",
    nextClick: "Select at least one product, answer the questions, then Generate.",
    spotlight: null,
    blocking: false,
  };
}

export function getFlexDemoCoachCopy(
  step: FlexDemoStep,
  collaborationsNavOpened: boolean,
  workspaceOpened = false,
  workspaceTab: FlexDemoWorkspaceTab = "overview",
): FlexDemoCoachCopy | null {
  if (step === "explore") {
    return {
      pageName: "Explore",
      pageIsFor: "This is where you find companies to collaborate with.",
      nextClick: "Click View company on GTsetu Demo Partner.",
      spotlight: "view-company",
    };
  }
  if (step === "preview") {
    return {
      pageName: "Company workspace",
      pageIsFor: "This is the same company profile you would open from Explore for a live partner.",
      nextClick: "Click Explore Opportunities.",
      spotlight: "express-interest-cta",
    };
  }
  if (step === "express-interest") {
    return resolveExpressInterestCoachFromDom();
  }
  if (step === "auto-accept") {
    return {
      pageName: "Match success",
      pageIsFor: "This is the same Match Success screen after both companies accept. Next you unlock the workspace.",
      nextClick: "Click Proceed to Payment.",
      spotlight: "continue-unlock",
    };
  }
  if (step === "pay") {
    return resolvePayCoachFromDom();
  }
  if (step === "workspace" && !collaborationsNavOpened) {
    return {
      pageName: "Collaborations",
      pageIsFor: "Unlocked workspaces live under Collaborations in the sidebar.",
      nextClick: "Click Collaborations in the left sidebar.",
      spotlight: "nav-collaborations",
    };
  }
  if (step === "workspace" && !workspaceOpened) {
    return {
      pageName: "Your collaborations",
      pageIsFor: "This is the same Collaborations list you use after unlock. Open the demo workspace from the card.",
      nextClick: "Click Open Workspace on GTsetu Demo Partner.",
      spotlight: "open-workspace",
    };
  }
  if (step === "workspace" && workspaceTab === "overview") {
    return {
      pageName: "Overview",
      pageIsFor:
        "This tab is the partner profile you see after both companies pay — company details, collaboration intent, and roles.",
      nextClick: "Click the Overview tab.",
      spotlight: "tab-overview",
      blocking: true,
    };
  }
  if (step === "workspace" && workspaceTab === "to-reports") {
    return {
      pageName: "Reports",
      pageIsFor: "Next is the joint collaboration report from both companies' match answers.",
      nextClick: "Click the Reports tab.",
      spotlight: "tab-reports",
      blocking: true,
    };
  }
  if (step === "workspace" && workspaceTab === "reports") {
    return {
      pageName: "Reports",
      pageIsFor:
        "This tab is the joint collaboration report. You can read the suggested path and download it.",
      nextClick: "Click the Reports tab.",
      spotlight: "tab-reports",
      blocking: true,
    };
  }
  if (step === "workspace" && workspaceTab === "to-workspace") {
    return {
      pageName: "Workspace",
      pageIsFor: "Next is the shared workspace for documents after unlock.",
      nextClick: "Click the Workspace tab.",
      spotlight: "tab-workspace",
      blocking: true,
    };
  }
  if (step === "workspace" && workspaceTab === "workspace") {
    return {
      pageName: "Workspace",
      pageIsFor: "This tab is where you share documents and work with the partner after unlock.",
      nextClick: "Click the Workspace tab.",
      spotlight: "tab-workspace",
      blocking: true,
    };
  }
  if (step === "workspace") {
    return {
      pageName: "Workspace",
      pageIsFor: "You have seen Overview, Reports, and Workspace.",
      nextClick: "Click Finish walkthrough.",
      spotlight: "finish-walkthrough",
      blocking: true,
    };
  }
  return {
    pageName: "Finish",
    pageIsFor: "You have walked Explore → Express Interest → Collaborations workspace.",
    nextClick: "Click Enter GTsetu to use the real app.",
    spotlight: null,
    blocking: false,
  };
}

export function isFlexDemoNavAllowed(
  navPath: string,
  step: FlexDemoStep,
): boolean {
  if (navPath === "/my-discovery" || navPath === FLEX_DEMO_PATH) {
    return isFlexDemoExploreStep(step);
  }
  if (navPath === "/collaborations") {
    return isFlexDemoWorkspaceStep(step);
  }
  return false;
}

export function flexDemoNavLockedTooltip(navPath: string): string {
  if (navPath.includes("shortlist")) return "You will use Shortlist after this demo to save companies.";
  if (navPath === "/home" || navPath.endsWith("/home")) {
    return "Home is where sent and received requests appear after this demo.";
  }
  if (navPath === "/profile") return "My Profile is your company page. You will use it after this demo.";
  if (navPath === MY_CLUSTER_PATH) return "My Cluster lists ecosystems you have joined after this demo.";
  if (navPath === "/updates") return "Updates is for notifications after this demo.";
  if (navPath.startsWith("/management")) return "Management tools are not part of this walkthrough.";
  if (navPath === "/collaborations") {
    return "Collaborations opens after you unlock the demo workspace.";
  }
  if (navPath === "/my-discovery") {
    return "Stay on Explore until you unlock the workspace.";
  }
  return "You will use this after the demo.";
}
